import { useCanvasById } from "@/api/canvas";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { OpenTab } from "@/context/OpenTabsContext";
import { useTabPreloadStatus } from "@/context/TabPreloadContext";
import { PreviewTooltipProvider } from "@/layouts/WorkspaceLayout/components/PreviewTooltipProvider";
import { workspacePopoverTooltipContentClassName } from "@/layouts/WorkspaceLayout/components/workspacePopoverTooltipClassName";
import { cn } from "@/utils";
import { getCanvasHeroImageUrl } from "@cohesive/api-client";
import { useThemeContext } from "@cohesive/ui-components";
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Frame,
  Image as ImageIcon,
  Loader2,
  MessageSquare,
  X,
} from "lucide-react";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

export const DEFAULT_W = 176;
export const ICON_W = 32;
export const GAP = 2;
export const ADD_RESERVE = 45;

export function computeTabLayout(
  available: number,
  exposedIdx: number,
  n: number,
): { width: number; ml: number; overlapping: boolean }[] | null {
  if (!n || !available) return null;
  if (n === 1) return [{ width: DEFAULT_W, ml: 0, overlapping: false }];

  const k = n - 1;
  const gaps = (n - 1) * GAP;
  let otherW = (available - DEFAULT_W - gaps) / k;
  let overlapping = false;
  let overlapPer = 0;
  if (otherW >= DEFAULT_W) {
    otherW = DEFAULT_W;
  } else if (otherW < ICON_W) {
    otherW = ICON_W;
    overlapping = true;
    overlapPer = (DEFAULT_W + k * ICON_W + gaps - available) / (n - 1);
  }

  return Array.from({ length: n }, (_, i) => ({
    width: Math.round(i === exposedIdx ? DEFAULT_W : otherW),
    ml:
      i === 0
        ? 0
        : Math.round((overlapping ? GAP - overlapPer : GAP) * 100) / 100,
    overlapping: overlapping && i !== exposedIdx,
  }));
}

function TabPreview({ label, imageUrl }: { label: string; imageUrl?: string }) {
  return (
    <div className="w-64">
      <div className="truncate px-0.5 pb-2 text-sm font-medium text-n-90">
        {label}
      </div>
      <div className="flex aspect-[16/9] w-full items-center justify-center overflow-hidden rounded-md border border-n-10 bg-n-05">
        {imageUrl ? (
          <img src={imageUrl} alt="" className="size-full object-cover" />
        ) : (
          <ImageIcon className="size-6 text-n-30" />
        )}
      </div>
    </div>
  );
}

type TabItemProps = {
  tab: OpenTab;
  active: boolean;
  raised: boolean;
  overlapping: boolean;
  closable: boolean;
  divider: boolean;
  exiting: boolean;
  suppressTooltip: boolean;
  style: React.CSSProperties;
  onSelect: () => void;
  onClose: (e: React.MouseEvent) => void;
  onContextMenu: (e: React.MouseEvent) => void;
  onMouseLeave: () => void;
  sortable?: boolean;
};

function TabItem({
  tab,
  active,
  raised,
  overlapping,
  closable,
  divider,
  exiting,
  suppressTooltip,
  style,
  onSelect,
  onClose,
  onContextMenu,
  onMouseLeave,
  sortable,
}: TabItemProps) {
  const [imgErr, setImgErr] = useState(false);
  const { mode } = useThemeContext();
  const preloadStatus = useTabPreloadStatus(tab.id);
  // `tab.id` is a strip-local placeholder while a create is in flight — there
  // is nothing to fetch or preview under it.
  const isPending = tab.kind === "canvas" && !!tab.pending;

  // The tab preview hero comes from the ["canvas", id] query: presigned hero
  // URLs are never stored (localStorage would freeze a ~1h-TTL signature that
  // silently rots), and the persisted query cache redacts them, so the entry
  // restores metadata-only and refetches. TabPreloadContext keeps this key
  // observed and fetched for every open canvas tab; the focus refetch keeps a
  // long-lived tab's presign working.
  const { data: freshCanvas } = useCanvasById(
    tab.kind === "canvas" && !isPending ? tab.id : undefined,
    {
      enabled: tab.kind === "canvas" && !isPending,
      staleTime: 30 * 60 * 1000,
      refetchOnWindowFocus: true,
    },
  );
  const targetUrl =
    tab.kind === "canvas" && !isPending
      ? getCanvasHeroImageUrl(freshCanvas, mode)
      : undefined;

  // Presigned URLs change on every re-fetch, and pointing the <img> at a
  // not-yet-downloaded URL blanks a working preview while it re-loads.
  // Double-buffer: keep showing the current URL and swap only once the new
  // one has finished loading (which also recovers from a rotted fallback).
  const [shownUrl, setShownUrl] = useState(targetUrl);
  useEffect(() => {
    if (!targetUrl) {
      // The canvas no longer has a hero (or the tab record dropped it) —
      // don't keep painting the old one.
      setShownUrl(undefined);
      return;
    }
    if (targetUrl === shownUrl) return;
    let cancelled = false;
    const probe = new Image();
    probe.onload = () => {
      if (cancelled) return;
      setShownUrl(targetUrl);
      setImgErr(false);
    };
    probe.src = targetUrl;
    return () => {
      cancelled = true;
    };
  }, [targetUrl, shownUrl]);

  const tabEl = (
    <div
      style={style}
      data-tab-id={tab.id}
      data-tab-kind={tab.kind}
      className={cn(
        "group relative box-border inline-flex h-8 shrink-0 items-center overflow-hidden whitespace-nowrap rounded-md text-base leading-none transition-[width,margin-left,opacity,background-color,border-color,color] duration-200 ease-out",
        exiting && "pointer-events-none",
        raised ? "z-20" : "z-0",
        active
          ? "bg-n-10 font-medium text-n-90"
          : overlapping
            ? "border border-n-10 bg-n-00 font-[450] text-n-50 hover:bg-n-05 hover:text-n-70"
            : "font-[450] text-n-50 hover:bg-n-05 hover:text-n-70",
        sortable && "touch-none",
      )}
      onContextMenu={onContextMenu}
      onMouseLeave={onMouseLeave}
    >
      {divider && (
        <span
          aria-hidden
          className="pointer-events-none absolute left-0 top-1/2 h-4 w-px -translate-y-1/2 bg-n-10 transition-opacity duration-150 group-hover:opacity-0"
        />
      )}
      <button
        type="button"
        onClick={onSelect}
        aria-current={active ? "page" : undefined}
        className="flex h-full min-w-0 flex-1 items-center gap-2 overflow-hidden px-2 leading-none outline-none focus-visible:outline-2 focus-visible:outline-rust focus-visible:outline-offset-[-2px]"
      >
        {shownUrl && !imgErr && (
          <img
            src={shownUrl}
            alt=""
            className="hidden"
            onError={() => setImgErr(true)}
          />
        )}
        {isPending || (preloadStatus === "loading" && !active) ? (
          <Loader2 className="size-3.5 shrink-0 animate-spin text-n-40" />
        ) : tab.kind === "chat" ? (
          <MessageSquare className="size-3.5 shrink-0 text-n-40" />
        ) : (
          <Frame
            className={cn(
              "size-3.5 shrink-0",
              active ? "text-n-30" : "text-n-40",
            )}
          />
        )}
        <span
          className={cn(
            "min-w-0 flex-1 overflow-hidden text-ellipsis text-left leading-none",
            closable &&
              "group-hover:[mask-image:linear-gradient(to_right,#000_calc(100%_-_32px),transparent_calc(100%_-_16px))] group-hover:[-webkit-mask-image:linear-gradient(to_right,#000_calc(100%_-_32px),transparent_calc(100%_-_16px))]",
          )}
        >
          {tab.name}
        </span>
      </button>
      {closable && (
        <button
          type="button"
          aria-label={`Close ${tab.name}`}
          onClick={(e) => {
            e.stopPropagation();
            onClose(e);
          }}
          className="absolute right-1.5 top-1/2 inline-flex size-4 -translate-y-1/2 items-center justify-center rounded-sm text-n-50 opacity-0 outline-none transition-opacity duration-150 hover:text-n-90 focus-visible:opacity-100 focus-visible:outline-1 focus-visible:outline-rust group-hover:opacity-100"
        >
          <X className="size-3.5" />
        </button>
      )}
    </div>
  );

  // The active tab previews too (Figma-style): uniform hover behavior, and
  // the card shows the full title even when the tab label is truncated.
  const showPreview = !exiting && !suppressTooltip && !isPending;
  return (
    // grouped: shares the strip's PreviewTooltipProvider warm state — first
    // preview delayed, sliding across neighboring tabs is instant (Chrome
    // tabs). No enter animation (`instant`): warm opens must pop, not fade.
    <Tooltip grouped open={suppressTooltip ? false : undefined}>
      <TooltipTrigger asChild>{tabEl}</TooltipTrigger>
      {showPreview && (
        <TooltipContent
          side="bottom"
          align="start"
          sideOffset={6}
          hideArrow
          instant
          className={cn("block p-2", workspacePopoverTooltipContentClassName)}
        >
          <TabPreview
            label={tab.name}
            imageUrl={shownUrl && !imgErr ? shownUrl : undefined}
          />
        </TooltipContent>
      )}
    </Tooltip>
  );
}

function SortableTabItem(props: TabItemProps) {
  const {
    setNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: props.tab.id });

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0 : undefined,
      }}
    >
      <TabItem
        {...props}
        sortable
        suppressTooltip={props.suppressTooltip || isDragging}
      />
    </div>
  );
}

export interface WorkspaceTabStripProps {
  tabs: OpenTab[];
  activeId: string | null;
  suppressTooltipForTab: string | null;
  draggingId: string | null;
  contextMenuTabId: string | null;
  onSelect: (tab: OpenTab) => void;
  onClose: (e: React.MouseEvent, id: string) => void;
  onContextMenu: (e: React.MouseEvent, tabId: string) => void;
  onMouseLeaveTab: (tabId: string) => void;
  newTabMenu: React.ReactNode;
}

type TabDisplayPhase = "in" | "enter" | "out";
type TabDisplayItem = { tab: OpenTab; phase: TabDisplayPhase };

export function WorkspaceTabStrip({
  tabs,
  activeId,
  suppressTooltipForTab,
  draggingId,
  contextMenuTabId,
  onSelect,
  onClose,
  onContextMenu,
  onMouseLeaveTab,
  newTabMenu,
}: WorkspaceTabStripProps) {
  const stripRef = useRef<HTMLDivElement>(null);
  const [stripW, setStripW] = useState(0);

  useLayoutEffect(() => {
    const el = stripRef.current;
    if (!el) return;
    const measure = () => {
      const w = Math.round(el.clientWidth);
      setStripW((prev) => (Math.abs(prev - w) < 1 ? prev : w));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const [display, setDisplay] = useState<TabDisplayItem[]>(() =>
    tabs.map((t) => ({ tab: t, phase: "in" })),
  );

  // Tabs arrive as one batch when the OpenTabsContext identity gate resolves
  // after load; that first batch must paint instantly, not fade in.
  const hydratedRef = useRef(tabs.length > 0);

  // Layout effect: reconciling after paint would flash one frame of the old
  // order right after a drag-reorder drop.
  useLayoutEffect(() => {
    const isFirstBatch = !hydratedRef.current && tabs.length > 0;
    if (tabs.length > 0) hydratedRef.current = true;
    setDisplay((prev) => {
      // Rebuild in `tabs` order so a drag-reorder sticks; phases only track
      // membership (enter/exit), never position.
      const prevById = new Map(prev.map((d) => [d.tab.id, d]));
      const kept = new Set(tabs.map((t) => t.id));
      // A settled create swaps a placeholder id for the real one in the same
      // slot. Inheriting the placeholder's phase keeps the tab at full width
      // instead of collapsing one and growing another where it stood.
      const departingPlaceholder = prev.find(
        (d) => d.tab.kind === "canvas" && d.tab.pending && !kept.has(d.tab.id),
      );
      let placeholderClaimed = false;
      const next: TabDisplayItem[] = tabs.map((t) => {
        const d = prevById.get(t.id);
        if (d) return { tab: t, phase: d.phase === "out" ? "enter" : d.phase };
        if (departingPlaceholder && !placeholderClaimed) {
          placeholderClaimed = true;
          return {
            tab: t,
            phase:
              departingPlaceholder.phase === "out"
                ? "enter"
                : departingPlaceholder.phase,
          };
        }
        return { tab: t, phase: isFirstBatch ? "in" : "enter" };
      });
      prev.forEach((d, i) => {
        if (kept.has(d.tab.id)) return;
        if (placeholderClaimed && d === departingPlaceholder) return;
        next.splice(Math.min(i, next.length), 0, { ...d, phase: "out" });
      });
      return next;
    });
  }, [tabs]);

  useEffect(() => {
    if (!display.some((d) => d.phase === "enter")) return;
    const id = requestAnimationFrame(() =>
      requestAnimationFrame(() =>
        setDisplay((prev) =>
          prev.map((d) => (d.phase === "enter" ? { ...d, phase: "in" } : d)),
        ),
      ),
    );
    return () => cancelAnimationFrame(id);
  }, [display]);

  useEffect(() => {
    if (!display.some((d) => d.phase === "out")) return;
    const id = setTimeout(
      () => setDisplay((prev) => prev.filter((d) => d.phase !== "out")),
      240,
    );
    return () => clearTimeout(id);
  }, [display]);

  const realTabs = display.filter((d) => d.phase !== "out").map((d) => d.tab);
  const exposedIdx = Math.max(
    0,
    realTabs.findIndex(
      (t) => t.id === (activeId ?? realTabs[realTabs.length - 1]?.id),
    ),
  );
  const layout = computeTabLayout(
    Math.max(0, stripW - ADD_RESERVE),
    exposedIdx,
    realTabs.length,
  );
  const layoutByKey: Record<
    string,
    { width: number; ml: number; overlapping: boolean }
  > = {};
  realTabs.forEach((t, i) => {
    if (layout) layoutByKey[t.id] = layout[i];
  });

  const dividerFor: Record<string, boolean> = {};
  let prevActive = false;
  let realCount = 0;
  for (const d of display) {
    if (d.phase === "out") {
      dividerFor[d.tab.id] = false;
      continue;
    }
    const L = layoutByKey[d.tab.id];
    const isActive = d.tab.id === activeId;
    dividerFor[d.tab.id] =
      realCount > 0 && !L?.overlapping && !isActive && !prevActive;
    prevActive = isActive;
    realCount += 1;
  }

  return (
    // One shared tooltip provider per strip so tab hover-previews share warm
    // state: first preview delayed, neighbors instant while sliding.
    <PreviewTooltipProvider>
      <div
        ref={stripRef}
        className="relative flex h-full min-w-0 flex-1 items-center overflow-hidden"
      >
        <SortableContext
          items={realTabs
            .filter((t) => !(t.kind === "canvas" && t.pending))
            .map((t) => t.id)}
          strategy={horizontalListSortingStrategy}
        >
          {display.map((d) => {
            const t = d.tab;
            const exiting = d.phase === "out";
            const collapsed = exiting || d.phase === "enter";
            const L = layoutByKey[t.id];
            const isActive = !exiting && t.id === activeId;
            // Nothing to close, reorder or act on until the id is real.
            const pending = t.kind === "canvas" && !!t.pending;
            const closable =
              !exiting && !pending && !L?.overlapping && (L?.width ?? 0) >= 72;
            const style: React.CSSProperties = {
              width: collapsed ? 0 : (L?.width ?? DEFAULT_W),
              marginLeft: exiting ? 0 : (L?.ml ?? GAP),
              opacity: collapsed ? 0 : 1,
            };
            const props: TabItemProps = {
              tab: t,
              active: isActive,
              raised:
                !exiting && t.id === (activeId ?? realTabs[exposedIdx]?.id),
              overlapping: !exiting && !!L?.overlapping,
              closable,
              divider: dividerFor[t.id],
              exiting,
              suppressTooltip:
                suppressTooltipForTab === t.id ||
                contextMenuTabId === t.id ||
                // any active drag: with instant previews, dragging a tab across
                // its neighbors would otherwise pop their previews
                draggingId !== null,
              style,
              onSelect: () => onSelect(t),
              onClose: (e) => onClose(e, t.id),
              onContextMenu: (e) => {
                if (pending) {
                  e.preventDefault();
                  return;
                }
                onContextMenu(e, t.id);
              },
              onMouseLeave: () => onMouseLeaveTab(t.id),
            };
            return exiting || pending ? (
              <TabItem key={t.id} {...props} />
            ) : (
              <SortableTabItem key={t.id} {...props} />
            );
          })}
        </SortableContext>

        <span className="mx-1.5 h-4 w-px shrink-0 bg-n-10" />
        {newTabMenu}
      </div>
    </PreviewTooltipProvider>
  );
}
